import { ROWS, TILE_PX, VIEW_W_PX } from '../shared/constants.ts';
import { T_SOLID, type Level } from '../shared/level.ts';

/**
 * The level's solid tiles. The sim only knows "solid", so the look is read
 * from the shape: ground changes material every ZONE_COLS columns, floating
 * one-tile rows are steel platforms, and narrow bumps sticking out of the
 * ground (the level's pillars) are stacked crates.
 */
const ZONE_COLS = 64;
const MATERIALS = ['turf', 'stone', 'track'] as const;
type Material = (typeof MATERIALS)[number];

const T = TILE_PX;
const S = T + 0.5; // a hair of overlap so tiles never show seams

export function drawTerrain(ctx: CanvasRenderingContext2D, level: Level, cam: number): void {
  const c0 = Math.floor(cam / T) - 1;
  const c1 = c0 + Math.ceil(VIEW_W_PX / T) + 2;
  // Height of the ground (solid tiles connected to the bottom) per column, with a margin for neighbours.
  const heights = new Map<number, number>();
  const height = (c: number): number => {
    let h = heights.get(c);
    if (h === undefined) {
      h = 0;
      while (h < ROWS && level.tile(c, ROWS - 1 - h) === T_SOLID) h++;
      heights.set(c, h);
    }
    return h;
  };
  const solid = (c: number, r: number) => level.tile(c, r) === T_SOLID;

  for (let c = c0; c <= c1; c++) {
    const x = c * T - cam;
    const h = height(c);
    const mat = MATERIALS[Math.floor(Math.max(0, c) / ZONE_COLS) % MATERIALS.length];
    const crateFrom = pillarBase(c, h, height);
    for (let r = 0; r < ROWS; r++) {
      if (!solid(c, r)) continue;
      const y = r * T;
      const fromBottom = ROWS - 1 - r;
      if (fromBottom >= h) {
        platform(ctx, x, y, !solid(c - 1, r), !solid(c + 1, r));
        continue;
      }
      if (crateFrom !== null && fromBottom >= crateFrom) {
        crate(ctx, x, y);
        continue;
      }
      const top = !solid(c, r - 1);
      body(ctx, mat, x, y, c, r);
      if (top) surface(ctx, mat, x, y, c);
      // Deeper is darker, and exposed sides get an edge.
      const depth = r - (ROWS - h);
      if (depth > 0) {
        ctx.fillStyle = `rgba(8, 5, 20, ${Math.min(0.45, depth * 0.11)})`;
        ctx.fillRect(x, y, S, S);
      }
      ctx.fillStyle = 'rgba(8, 5, 20, 0.35)';
      if (!solid(c - 1, r)) ctx.fillRect(x, y, 1, S);
      if (!solid(c + 1, r)) ctx.fillRect(x + T - 1, y, 1, S);
    }
  }
}

/** For a narrow bump (1–2 columns) above ground on both sides: the row (from the bottom) where crates start. */
function pillarBase(c: number, h: number, height: (c: number) => number): number | null {
  if (h === 0) return null;
  const twinL = height(c - 1) === h;
  const twinR = height(c + 1) === h;
  if (twinL && twinR) return null;
  const left = height(twinL ? c - 2 : c - 1);
  const right = height(twinR ? c + 2 : c + 1);
  if ((twinL && height(c - 2) === h) || (twinR && height(c + 2) === h)) return null;
  if (left === 0 || right === 0 || left >= h || right >= h) return null;
  return Math.max(left, right);
}

function hash(a: number, b: number): number {
  let h = Math.imul(a ^ 0x27d4eb2d, 0x85ebca6b) ^ Math.imul(b + 0x165667b1, 0xc2b2ae35);
  h ^= h >>> 15;
  h = Math.imul(h, 0x2c1b3c6d);
  h ^= h >>> 12;
  return h >>> 0;
}

function body(ctx: CanvasRenderingContext2D, mat: Material, x: number, y: number, c: number, r: number): void {
  const h = hash(c, r);
  switch (mat) {
    case 'turf': {
      // Rich soil with stones.
      ctx.fillStyle = (c + r) % 2 ? '#8a5a3a' : '#7f5235';
      ctx.fillRect(x, y, S, S);
      if (h % 3 === 0) {
        const sx = x + 2 + (h >>> 3) % 10;
        const sy = y + 3 + (h >>> 7) % 9;
        ctx.fillStyle = '#5f3c26';
        ctx.fillRect(sx, sy + 1, 4, 2);
        ctx.fillStyle = '#b48a64';
        ctx.fillRect(sx, sy, 4, 2);
      }
      break;
    }
    case 'stone': {
      // Blue-grey blocks, staggered.
      ctx.fillStyle = '#66729a';
      ctx.fillRect(x, y, S, S);
      ctx.fillStyle = '#7682ab';
      ctx.fillRect(x, y, S, 1);
      ctx.fillRect(x, y + 8, S, 1);
      ctx.fillStyle = '#4d5779';
      ctx.fillRect(x, y + 7, S, 1);
      ctx.fillRect(x, y + 15, S, 1);
      const off = (c + r) % 2 ? 4 : 12;
      ctx.fillRect(x + off, y, 1, 7);
      ctx.fillRect(x + ((off + 8) % 16), y + 8, 1, 7);
      break;
    }
    case 'track': {
      // Asphalt with speckles.
      ctx.fillStyle = '#4a4466';
      ctx.fillRect(x, y, S, S);
      ctx.fillStyle = '#5c5580';
      ctx.fillRect(x + (h % 14) + 1, y + ((h >>> 4) % 14) + 1, 1.5, 1.5);
      ctx.fillRect(x + ((h >>> 8) % 14) + 1, y + ((h >>> 12) % 14) + 1, 1.5, 1.5);
      ctx.fillStyle = '#3a3554';
      ctx.fillRect(x + ((h >>> 16) % 14) + 1, y + ((h >>> 20) % 14) + 1, 2, 1);
      break;
    }
  }
}

function surface(ctx: CanvasRenderingContext2D, mat: Material, x: number, y: number, c: number): void {
  switch (mat) {
    case 'turf': {
      // Mowed stripes, a bright lip, and grass hanging over the soil.
      ctx.fillStyle = Math.floor(c / 2) % 2 ? '#3fb05a' : '#36a050';
      ctx.fillRect(x, y, S, 5);
      ctx.fillStyle = '#7ee08c';
      ctx.fillRect(x, y, S, 1);
      ctx.fillStyle = '#2d8a45';
      const h = hash(c, 77);
      for (let i = 0; i < 3; i++) ctx.fillRect(x + ((h >>> (i * 4)) % 14) + 1, y + 5, 2, 1 + ((h >>> (i * 3 + 13)) % 2));
      break;
    }
    case 'stone': {
      ctx.fillStyle = '#c9d0e6';
      ctx.fillRect(x, y, S, 4);
      ctx.fillStyle = '#eef1fa';
      ctx.fillRect(x, y, S, 1);
      ctx.fillStyle = '#8f98b8';
      ctx.fillRect(x, y + 4, S, 1);
      break;
    }
    case 'track': {
      // A running track: red tartan with a white lane line.
      ctx.fillStyle = '#e2563f';
      ctx.fillRect(x, y, S, 5);
      ctx.fillStyle = '#ff8a6e';
      ctx.fillRect(x, y, S, 1);
      ctx.fillStyle = 'rgba(255, 245, 235, 0.9)';
      ctx.fillRect(x, y + 2, S, 1);
      ctx.fillStyle = '#a63a2c';
      ctx.fillRect(x, y + 5, S, 1);
      break;
    }
  }
}

/** A floating steel girder, with a shadow under it. */
function platform(ctx: CanvasRenderingContext2D, x: number, y: number, leftEnd: boolean, rightEnd: boolean): void {
  const l = leftEnd ? 1 : 0;
  const w = S - l - (rightEnd ? 1 : 0);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
  ctx.fillRect(x + (leftEnd ? 3 : 0), y + 12, S - (leftEnd ? 3 : 0) - (rightEnd ? 3 : 0), 3);
  ctx.fillStyle = '#5d6a88';
  ctx.fillRect(x + l, y, w, 12);
  ctx.fillStyle = '#a9b8d6';
  ctx.fillRect(x + l, y + 1, w, 9);
  ctx.fillStyle = '#eef3ff';
  ctx.fillRect(x + l, y, w, 2);
  // Truss holes and rivets.
  ctx.fillStyle = '#6f7d9c';
  ctx.beginPath();
  ctx.moveTo(x + 2, y + 9);
  ctx.lineTo(x + 7, y + 4);
  ctx.lineTo(x + 7, y + 9);
  ctx.moveTo(x + 9, y + 4);
  ctx.lineTo(x + 14, y + 4);
  ctx.lineTo(x + 9, y + 9);
  ctx.fill();
  if (leftEnd) {
    ctx.fillStyle = '#eef3ff';
    ctx.fillRect(x + 2, y + 3, 1.5, 1.5);
  }
  if (rightEnd) {
    ctx.fillStyle = '#eef3ff';
    ctx.fillRect(x + T - 3.5, y + 3, 1.5, 1.5);
  }
}

/** A wooden crate. */
function crate(ctx: CanvasRenderingContext2D, x: number, y: number): void {
  ctx.fillStyle = '#7a4a24';
  ctx.fillRect(x, y, S, S);
  ctx.fillStyle = '#d0904f';
  ctx.fillRect(x + 2, y + 2, 12, 12);
  ctx.strokeStyle = '#9c6534';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x + 3, y + 13);
  ctx.lineTo(x + 13, y + 3);
  ctx.stroke();
  ctx.fillStyle = '#f0b878';
  ctx.fillRect(x, y, S, 1);
}
